import React from 'react';
import { string, number, shape, arrayOf } from 'prop-types';
import { isNil } from 'ramda';
import { connect } from 'react-redux';
import dateFormat from 'date-fns/format';

import { capitalize } from '../../utils/helpers';
import {
  getPrecision,
  getCompare,
  getSafeMetric,
} from '../../selectors/options';
import {
  getTotalTimespanInterval,
  getTotalCompareInterval,
} from '../../selectors/intervals';
import { getCrimeTypeText } from '../../selectors/crimes';
import {
  valueFormat,
  signedValueFormat,
  timeIntervalPrecisionFormat,
  monthRangeFormat,
  yearRangeFormat,
  recurringRangeFormat,
} from '../../utils/format';
import { Compare, VisType, Metric, Precision } from '../../constants';
import { isCensusView } from '../../selectors/router';

import GenericTooltip from './GenericTooltip';

const compareType = {
  [Compare.NONE]: 'one',
  [Compare.YEAR]: 'two',
  [Compare.PERIOD]: 'two',
  [Compare.RECURRING]: 'recurring',
  [Compare.CUSTOM]: 'two',
};

const metricDisplay = censusView => {
  return {
    [Metric.RATE]: censusView
      ? 'rate per 1k residents'
      : 'rate per 100k residents',
    [Metric.TOTAL]: 'total',
  };
};

const joinIfShort = ([line1, line2], threshold) =>
  line1.length + line2.length < threshold
    ? [line1 + ' ' + line2]
    : [line1, line2];

export const formatMapDateLines = {
  one: ({ precision, timespanInterval, customTimespanInterval }) => [
    timeIntervalPrecisionFormat[precision](
      timespanInterval || customTimespanInterval,
    ),
  ],
  two: ({ highlights, customTimespanInterval, compareInterval, precision }) =>
    highlights
      ? []
      : joinIfShort(
          [
            timeIntervalPrecisionFormat[precision](customTimespanInterval),
            'compared to ' +
              timeIntervalPrecisionFormat[precision](compareInterval),
          ],
          30,
        ),
  recurring: ({
    precision,
    customTimespanInterval,
    timespanInterval,
    compareInterval,
  }) =>
    precision === Precision.MONTHLY
      ? [
          'Comparison of ' + monthRangeFormat(customTimespanInterval),
          'with the Average of ' +
            yearRangeFormat(compareInterval || timespanInterval),
        ]
      : [
          'Comparison of ' + yearRangeFormat(customTimespanInterval),
          'with the Average of ' +
            yearRangeFormat(compareInterval || timespanInterval),
        ],
};

export const formatDateLines = {
  [VisType.LINE]: ({ timestamp, precision }) => [
    dateFormat(timestamp, precision === Precision.YEARLY ? 'YYYY' : 'MMM YYYY'),
  ],
  [VisType.SINGLE]: ({ precision, timespanInterval }) => [
    timeIntervalPrecisionFormat[precision](timespanInterval),
  ],
  [VisType.DIFFERENCE]: () => [],
  [VisType.COMPARISON]: ({ precision, timespanInterval }) => [
    timeIntervalPrecisionFormat[precision](timespanInterval),
  ],
  [VisType.RECURRING]: ({ customTimespanInterval, precision, year }) => [
    precision === Precision.YEARLY
      ? String(year)
      : recurringRangeFormat(customTimespanInterval, year),
  ],
  [VisType.MAP]: props => formatMapDateLines[compareType[props.compare]](props),
};

// The problems with this is that is solves nothing. Can be inside JSX all the same.
export const tooltipProps = ({
  visType,
  crimeTypeText,
  precision,
  metric,
  compare,
  mainValue,
  customTimespanInterval,
  timespanInterval,
  compareInterval,
  timestamp,
  year,
  highlights,
  isPartial,
  censusView,
}) => ({
  compare,
  mainValue:
    compareType[compare] === 'two' &&
    visType !== VisType.COMPARISON &&
    visType !== VisType.SINGLE
      ? signedValueFormat(mainValue)
      : valueFormat(mainValue),
  lines: [
    isNil(mainValue)
      ? 'No data'
      : isPartial
        ? 'Partial data'
        : capitalize(crimeTypeText) + ' ' + metricDisplay(censusView)[metric],

    ...formatDateLines[visType]({
      compare,
      precision,
      customTimespanInterval,
      timespanInterval,
      compareInterval,
      timestamp,
      year,
      highlights,
    }),
  ],
  tableRows: highlights
    ? highlights.map(({ value, timespan }) => ({
        value: valueFormat(value),
        text: timeIntervalPrecisionFormat[precision](timespan),
      }))
    : [],
});

export const GenericTooltipContainer = ({
  sideColor,
  title,
  subtitle,
  link,
  hoverable,
  has_census_data,
  ...props
}) => (
  <GenericTooltip
    sideColor={sideColor}
    title={title}
    subtitle={subtitle}
    link={link}
    hoverable={hoverable}
    has_census_data={has_census_data}
    {...tooltipProps(props)}
  />
);

GenericTooltipContainer.propTypes = {
  visType: string.isRequired,
  timespanInterval: string,
  timestamp: number,
  year: number,
  highlights: arrayOf(
    shape({
      timespan: string.isRequired,
      value: number,
    }),
  ),
  ...GenericTooltip.propTypes,
};

export default connect(state => ({
  crimeTypeText: getCrimeTypeText(state),
  precision: getPrecision(state),
  metric: getSafeMetric(state),
  compare: getCompare(state),
  customTimespanInterval: getTotalTimespanInterval(state),
  compareInterval: getTotalCompareInterval(state),
  censusView: isCensusView(state),
}))(GenericTooltipContainer);



// WEBPACK FOOTER //
// ./src/components/GenericTooltip/GenericTooltipContainer.jsx